import axios from 'axios'

/**
 * API host for public calendar requests (no trailing slash).
 * Always VUE_APP_API_URL (e.g. https://dev.clarus-care.com) — never
 * VUE_APP_CALENDAR_SHARE_URL, which is only the public SPA origin.
 * `/api` is always appended; paths start with `v1/...`.
 */
const host = (process.env.VUE_APP_API_URL || '').trim().replace(/\/$/, '')

const baseURL = host ? `${host}/api` : '/api'

let publicClientPromise = null

/**
 * Request an OAuth access token for the public calendar client.
 *
 * @return {Promise<Object>}
 */
async function getInitialAccessToken() {
	return axios.post(
		`${baseURL}/v1/oauth/token`,
		{ grant_type: 'client_credentials', scope: 'public-calendar' },
		{
			headers: {
				Authorization: `Basic ${btoa(
					`${process.env.VUE_APP_PUBLIC_CALENDAR_CLIENT_ID}:${process.env.VUE_APP_PUBLIC_CALENDAR_CLIENT_SECRET}`
				)}`,
				'Content-Type': 'application/json',
			},
		}
	)
}

/**
 * Create an axios client authorized with the public calendar token.
 *
 * @return {Promise<import('axios').AxiosInstance>}
 */
async function createPublicClient() {
	const response = await getInitialAccessToken()
	const accessToken = response?.data?.access_token

	if (!accessToken) {
		throw new Error(
			'Public calendar token request did not return an access token.'
		)
	}

	return axios.create({
		baseURL,
		headers: {
			Accept: 'application/json',
			Authorization: `Bearer ${accessToken}`,
		},
	})
}

/**
 * Lazily initialize the public API client after the token call completes.
 *
 * @return {Promise<import('axios').AxiosInstance>}
 */
function getPublicClient() {
	if (!publicClientPromise) {
		publicClientPromise = createPublicClient()
	}

	return publicClientPromise
}

/**
 * Public calendar API client. Methods wait for the token response before
 * calling bootstrap/events endpoints.
 */
const publicClient = {
	get(url, config) {
		return getPublicClient().then(client => client.get(url, config))
	},

	post(url, data, config) {
		return getPublicClient().then(client => client.post(url, data, config))
	},
}

export default publicClient
